import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useThreadMarkRead } from '../../../shared/notifications';
import { ticketApi, portalApi } from '../services/supportApi';
import type { TicketCreate, TicketUpdate, TicketMessageCreate, ListTicketsParams, PortalTicketCreate } from '../types';

export const ticketKeys = {
  all: ['tickets'] as const,
  list: (params?: ListTicketsParams) => [...ticketKeys.all, 'list', params] as const,
  detail: (id: string) => [...ticketKeys.all, 'detail', id] as const,
  messages: (id: string) => [...ticketKeys.all, 'messages', id] as const,
  stats: (agentId?: string, groupId?: string) => [...ticketKeys.all, 'stats', agentId, groupId] as const,
};

function cleanParams(params?: ListTicketsParams): ListTicketsParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as ListTicketsParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function useTickets(params?: ListTicketsParams) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only recompute when specific fields change
  const stableParams = useMemo(() => cleanParams(params), [
    params?.status,
    params?.priority,
    params?.assigned_agent_id,
    params?.assigned_group_id,
    params?.contact_id,
    params?.organization_id,
    params?.channel,
    params?.product_id,
    params?.search,
    params?.include_closed,
    params?.tags,
    params?.tags_mode,
    params?.custom_fields,
    params?.offset,
    params?.limit,
    params?.admin_scope,
  ]);

  return useQuery({
    queryKey: ticketKeys.list(stableParams),
    queryFn: () => ticketApi.list(stableParams),
  });
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    queryFn: () => ticketApi.get(id),
    enabled: !!id,
  });
}

export function useTicketMessages(id: string, includeInternal = true) {
  return useQuery({
    queryKey: ticketKeys.messages(id),
    queryFn: () => ticketApi.getMessages(id, includeInternal),
    enabled: !!id,
  });
}

export function useTicketStats(agentId?: string, groupId?: string) {
  return useQuery({
    queryKey: ticketKeys.stats(agentId, groupId),
    queryFn: () => ticketApi.getStats(agentId, groupId),
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TicketCreate) => ticketApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useUpdateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TicketUpdate }) => ticketApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useAssignTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, agentId, groupId }: { id: string; agentId?: string; groupId?: string }) =>
      ticketApi.assign(id, agentId, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useAddTicketMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: TicketMessageCreate }) =>
      ticketApi.addMessage(ticketId, data),
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.messages(ticketId) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
    },
  });
}

/**
 * Mark a ticket's message thread read for the current agent (clears the unread
 * badge). Wraps the generic `useThreadMarkRead` seam — call `markAsRead()` when
 * the Messages tab opens; it invalidates the ticket detail so `unread_count`
 * refetches to 0.
 */
export function useMarkTicketRead(ticketId: string) {
  return useThreadMarkRead({
    markRead: () => ticketApi.markRead(ticketId),
    invalidate: ticketKeys.detail(ticketId),
    enabled: !!ticketId,
  });
}

export function useSubmitCsat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, score, comment }: { ticketId: string; score: number; comment?: string }) =>
      ticketApi.submitCsat(ticketId, score, comment),
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
    },
  });
}

export function useLinkedWorkItem(ticketId: string) {
  return useQuery({
    queryKey: [...ticketKeys.all, 'linked-work-item', ticketId] as const,
    queryFn: () => ticketApi.getLinkedWorkItem(ticketId),
    enabled: !!ticketId,
  });
}

export function useCreateWorkItemFromTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) => ticketApi.createWorkItem(ticketId),
    onSuccess: (_, ticketId) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
      queryClient.invalidateQueries({ queryKey: [...ticketKeys.all, 'linked-work-item', ticketId] });
    },
  });
}

export function useDeleteTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ticketApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useBulkUpdateTickets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, data }: { ids: string[]; data: TicketUpdate }) =>
      ticketApi.bulkUpdate(ids, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useBulkDeleteTickets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => ticketApi.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

// Portal hooks (for external users)
export const portalTicketKeys = {
  all: ['portal-tickets'] as const,
  list: (params?: ListTicketsParams) => [...portalTicketKeys.all, 'list', params] as const,
  detail: (id: string) => [...portalTicketKeys.all, 'detail', id] as const,
  messages: (id: string) => [...portalTicketKeys.all, 'messages', id] as const,
};

export function usePortalTickets(params?: ListTicketsParams) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only recompute when specific fields change
  const stableParams = useMemo(() => cleanParams(params), [
    params?.status,
    params?.search,
    params?.offset,
    params?.limit,
  ]);

  return useQuery({
    queryKey: portalTicketKeys.list(stableParams),
    queryFn: () => portalApi.listTickets(stableParams),
  });
}

export function usePortalTicket(id: string) {
  return useQuery({
    queryKey: portalTicketKeys.detail(id),
    queryFn: () => portalApi.getTicket(id),
    enabled: !!id,
  });
}

export function usePortalTicketMessages(id: string) {
  return useQuery({
    queryKey: portalTicketKeys.messages(id),
    queryFn: () => portalApi.getMessages(id),
    enabled: !!id,
  });
}

export function useCreatePortalTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PortalTicketCreate) => portalApi.createTicket(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalTicketKeys.all });
    },
  });
}

export function useAddPortalTicketMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, body, bodyHtml }: { ticketId: string; body: string; bodyHtml?: string }) =>
      portalApi.addMessage(ticketId, body, bodyHtml),
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: portalTicketKeys.messages(ticketId) });
      queryClient.invalidateQueries({ queryKey: portalTicketKeys.detail(ticketId) });
    },
  });
}

export function usePortalTicketAttachments(ticketId: string) {
  return useQuery({
    queryKey: [...portalTicketKeys.all, 'attachments', ticketId] as const,
    queryFn: () => portalApi.listAttachments(ticketId),
    enabled: !!ticketId,
  });
}

export function useResolvePortalTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) => portalApi.resolveTicket(ticketId),
    onSuccess: (_, ticketId) => {
      queryClient.invalidateQueries({ queryKey: portalTicketKeys.detail(ticketId) });
      queryClient.invalidateQueries({ queryKey: portalTicketKeys.list() });
    },
  });
}

export function useDownloadPortalTicketAttachment() {
  return useMutation({
    mutationFn: async ({ ticketId, attachmentId, filename }: { ticketId: string; attachmentId: string; filename: string }) => {
      const blob = await portalApi.downloadAttachment(ticketId, attachmentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

export function usePortalProducts() {
  return useQuery({
    queryKey: ['portal-products'] as const,
    queryFn: () => portalApi.listProducts(),
  });
}
